import type { Ref } from 'vue';

import { type BackendErrorCode, type DealerSaleItemEntity, type DealerSaleListingItemEntity } from '~/apiClient';
import type { InspectionExpiryStatus, SaleStatus } from '~/types/saleStatus';

export default function useDealerSaleStatus(sale: Ref<DealerSaleItemEntity | DealerSaleListingItemEntity>): SaleStatus {
    const { tt } = useTypedI18n();
    const { companyScopeIds } = useAuthUtils();
    const dayjs = useDayjs();

    const isSeller = computed(() => isSellerOfSale(sale.value, companyScopeIds.value));
    // Always sold: the order is created with the sale, so there is no "available" state. `orderId` is only
    // set when the viewer's company is the buyer.
    const hasOrder = computed(() => 'orderId' in sale.value && !!sale.value.orderId);

    const statusSectionColor = computed(() => (hasOrder.value || isSeller.value ? 'bg-success-50' : 'bg-neutral-100'));

    const title = computed(() => (hasOrder.value ? tt('sdp.DirectSale.directSaleStatus.purchased') : tt('sdp.DirectSale.directSaleStatus.sold')));

    const titleColor = computed<string | undefined>(() => (hasOrder.value || isSeller.value ? 'text-success-600' : undefined));

    const titleAnnotation = computed<string | undefined>(() => undefined);

    const titleAnnotationColor = computed<string | undefined>(() => undefined);

    const priceColor = computed<string | undefined>(() => (hasOrder.value || isSeller.value ? 'text-success-800' : 'text-default'));

    const statusDotColor = computed<string | undefined>(() => 'bg-neutral-500');

    const isFeeNoticeHidden = computed(() => true);

    const hasRestrictedNotice = computed(() => false);

    const isBailNeeded = computed(() => false);

    const hasInspectionNotice = computed(() => false);

    const hasPurchaseBlock = computed(() => false);

    const purchaseBlockReasons = computed<BackendErrorCode[]>(() => []);

    const inspectionReminder = computed<InspectionExpiryStatus | undefined>(() => {
        if (!isSeller.value) return undefined;

        if (!sale.value.mainAsset.inspectionExpiryDate) return undefined;

        const now = dayjs();
        const target = dayjs(sale.value.mainAsset.inspectionExpiryDate);

        if (target.isBefore(now)) return 'expired';
        if (target.isBefore(now.add(30, 'day'))) return 'expiringSoon';

        return undefined;
    });

    return {
        statusSectionColor,
        title,
        titleColor,
        titleAnnotation,
        titleAnnotationColor,
        priceColor,
        statusDotColor,
        isFeeNoticeHidden,
        hasRestrictedNotice,
        isBailNeeded,
        hasInspectionNotice,
        hasPurchaseBlock,
        purchaseBlockReasons,
        isSeller,
        inspectionReminder,
    };
}
